import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs, { Dayjs } from 'dayjs';
import { Box, ClosableDrawer } from '@atoms/layout';
import { useDirectQualificationsStore, useOrgQualificationsStore } from '@libs/store/settings';
import { Chip, Grid, Stack, styled, Typography } from '@mui/material';
import { Button } from '@atoms/buttons';
import { useForm } from 'react-hook-form';
import {
  CollapsibleFormBlock,
  FormBlock,
  FormCheckbox,
  FormDatePicker,
  FormNumberField,
  FormRadioGroup,
  FormSelect,
  FormTextField,
  FormTimePicker,
  Form,
} from '@molecules/form';
import { useLocationStore } from '@libs/store/locations';
import { SingleShift } from '@libs/models/shifts/shift';
import { useNotification } from '@libs/snackbar';
import {
  useShiftRotationAddEditStore,
  useShiftDrawerTalents,
  useShareDrawerStore,
  useShiftsAssignment,
} from '@libs/store/shifts';
import { SequenceItem, ShiftRotationUpdate } from '@libs/models/shifts/rotation';
import { RotationScheduleSection } from './rotationScheduleSection';
import { RotationSequenceSection } from './rotationSequenceSection';
import { parseError } from '@libs/api/errors';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { useShiftTemplatesList } from '@libs/store/shiftTemplates';
import { useShiftTypePresetsList } from '@libs/store/shiftTypePresets';
import { AssigneesSelect, QualificationSelect } from '../components';
import { LocationSelectionSection } from '@pages/schedule/components';
import { DrawerControlButton } from '@molecules/drawers';

type EditSeriesDrawerProps = {
  onSaveCall?: () => void;
};
// TODO: rename to `create` because for editing we use another UI
export function EditRotationDrawer({ onSaveCall }: EditSeriesDrawerProps) {
  const intl = useIntl();
  const { showErrorIntl } = useNotification();
  const openShiftShareDrawer = useShareDrawerStore((s) => s.openShiftShareDrawer);

  const {
    drawerOpen,
    loading: rotationLoading,
    data: currentRotationDetails,
    rotationId,
    closeShiftRotationDrawer,
    load,
    drawerMode,
    createRotation,
  } = useShiftRotationAddEditStore();
  const { loading: locationsLoading, currentLocationID, loadSimple, current: currentLocation } = useLocationStore();
  const { requestAssignShiftOnExternal } = useShiftsAssignment();

  useEffect(() => {
    if (!currentLocationID) {
      loadSimple();
    }
  }, [currentLocationID]);

  const qualStore = useDirectQualificationsStore();
  const { load: loadDirectQualifications, loading: isDirectListLoading } = qualStore;
  const directQualifications = qualStore.data || [];

  const orgQualStore = useOrgQualificationsStore();
  const { orgQualifications, load: loadOrgQuals, loading: isOrgQualListLoading } = orgQualStore;

  const { load: loadProviders, talents, loading: talentsLoading } = useShiftDrawerTalents();

  const {
    initialized: templatesListInitialized,
    load: loadShiftTemplates,
    list: shiftTemplates,
    deinit: deinitShiftTemplatesList,
  } = useShiftTemplatesList();

  const {
    initialized: shiftTypePresetsInitialized,
    load: loadShiftTypePresets,
    list: shiftTypePresets,
    deinit: deinitShiftTypePresetsList,
  } = useShiftTypePresetsList();

  const [incomingSequence, setIncomingSequence] = useState<SequenceItem[]>([]);
  const [sequence, setSequence] = useState<SequenceItem[]>([{ isPause: false, durationInDays: 1, order: 0 }]);
  const [showArchivedWarn, setShowArchivedWarn] = useState<boolean>(false);
  const [showSemiArchivedWarn, setShowSemiArchivedWarn] = useState<boolean>(false);

  const isAddDialog = drawerMode == 'add';
  const localizationSubset = isAddDialog ? 'shifts.addRotationDrawer' : 'shifts.editRotationDrawer';
  const open = drawerOpen;

  const loading =
    rotationLoading ||
    isOrgQualListLoading ||
    talentsLoading ||
    locationsLoading ||
    isDirectListLoading ||
    !templatesListInitialized ||
    !shiftTypePresetsInitialized;

  useEffect(() => {
    deinitShiftTemplatesList();
    deinitShiftTypePresetsList();
  }, [deinitShiftTemplatesList, deinitShiftTypePresetsList]);

  useEffect(() => {
    if (!templatesListInitialized) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadShiftTemplates({
        location: {
          key: 'location',
          value: [currentLocationID],
        },
      });
    }
  }, [templatesListInitialized, loadShiftTemplates]);

  useEffect(() => {
    if (!shiftTypePresetsInitialized) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadShiftTypePresets({
        location: {
          key: 'location',
          value: [currentLocationID],
        },
      });
    }
  }, [shiftTypePresetsInitialized, loadShiftTypePresets]);

  const initial: ShiftRotationUpdate = {
    numberOfCopies: 1, // positive int
    assignees: [], // array of ids
    staffingType: 'All',
    startDate: dayjs(new Date()).toServerDateTimeFormat(), // here should be a date from the server on Edit operation
    endDate: dayjs(new Date()).toServerDateTimeFormat(), // here should be a date from the server on Edit operation
    dateTimeSlots: [],
    sequence: [],
    departmentNodeId: '',
    specialtyId: '',
    locationId: '',
    timeZone: '',
    staffNurseQualificationFilterIds: [], // Qualification ids
    staffOtherQualificationFilterIds: [], // Qualification ids
    tags: [],
    note: '',
    isUrgent: false,
    subDepartmentNodeId: '',
    isNeedToPublish: false,
  };

  const [currentShift, setCurrentShift] = useState<ShiftRotationUpdate>({
    ...initial,
    ...(currentRotationDetails || {}),
  });

  const methods = useForm<ShiftRotationFormData>({
    defaultValues: {
      template: '',
      numberOfCopies: 1,
      isUrgent: currentShift.isUrgent,

      durationType: 'setEndDate',
      startDate: null, //dayjs(currentShift.startDate),
      endDate: null, //dayjs(currentShift.endDate),

      seriesPreset: '',
      shiftTypePreset: '',
      startTime: null, //dayjs(currentShift.startDate),
      endTime: null, //dayjs(currentShift.startDate),

      location: currentLocationID,
      department: '',
      subDepartment: '',
      specialty: '',

      staffingType: 'All',
      qualifications: [],
      assignees: [],

      tags: '',
      notes: '',

      schedule: [],
    },
  });
  const {
    watch,
    setValue,
    register,
    formState: { errors },
  } = methods;
  const dateTimeSlots = watch('schedule');

  useEffect(() => {
    register('schedule', {
      validate: (value) => {
        const isValidSchedule = !checkOverlapping(value);
        if (!isValidSchedule) {
          return intl.formatMessage({ id: `${localizationSubset}.errors.overlapping` });
        }

        return true;
      },
    });
  }, [intl, localizationSubset, register]);

  const scheduledUniqueDays = Array.from(new Set(dateTimeSlots.map((slot) => slot.start?.format('YYYY-MM-DD') || '')));

  useEffect(() => {
    if (rotationId) {
      load(rotationId);
    }
  }, [rotationId]);

  useEffect(() => {
    if (!orgQualifications) {
      loadOrgQuals();
    }
  }, [orgQualifications]);

  useEffect(() => {
    setCurrentShift({
      ...currentShift,
      ...currentRotationDetails,
    });
  }, [currentRotationDetails]);

  useEffect(() => {
    if (!directQualifications.length) {
      loadDirectQualifications();
    }
  }, [loadDirectQualifications]);

  const handleClose = () => {
    closeShiftRotationDrawer();
    methods.reset();
  };

  const handleSave = async () => {
    await methods.handleSubmit(handleSubmit.bind(handleSubmit, false, false))();
    onSaveCall && onSaveCall();
  };

  const handlePublish = async () => {
    await methods.handleSubmit(handleSubmit.bind(handleSubmit, true, false))();
    onSaveCall && onSaveCall();
  };

  const handlePublishNShare = async () => {
    await methods.handleSubmit(handleSubmit.bind(handleSubmit, true, true))();
    onSaveCall && onSaveCall();
  };

  const handleSubmit = async (
    shouldPerformPublish: boolean,
    shouldOpenShareAfterPublish: boolean,
    data: ShiftRotationFormData,
  ) => {
    const tagsParsed = (data.tags || '').split(',').map((tag) => tag.trim());
    const assignees = data.assignees.map((value) => talents.find((t) => t.id === value));
    const dataToSend: ShiftRotationUpdate = {
      numberOfCopies: data.numberOfCopies,
      assignees: assignees.filter((talent) => talent?.staffingType !== 'Agency').map((t) => t!.id),
      staffingType: data.staffingType,
      startDate: data.startDate!.toServerDateTimeFormat(),
      endDate: data.endDate!.toServerDateTimeFormat(),
      sequence: sequence,
      dateTimeSlots: data.schedule.map((slot) => {
        if (slot.start.isAfter(slot.end)) {
          // If start date is after end date, shift ends on the next day
          return {
            startDate: slot.start.toServerDateTimeFormat(),
            endDate: slot.end.add(1, 'day').toServerDateTimeFormat(),
          };
        }
        return {
          startDate: slot.start.toServerDateTimeFormat(),
          endDate: slot.end.toServerDateTimeFormat(),
        };
      }),
      departmentNodeId: data.department,
      specialtyId: data.specialty,
      locationId: data.location,
      timeZone: currentLocation?.timezone ?? TIMEZONE_FALLBACK,
      staffNurseQualificationFilterIds: data.qualifications,
      staffOtherQualificationFilterIds: [],
      tags: tagsParsed,
      note: data.notes || '',
      isUrgent: !!data.isUrgent,
      subDepartmentNodeId: data.subDepartment,
      isNeedToPublish: shouldPerformPublish,
    };
    try {
      const listOfRotations = await createRotation(dataToSend);
      if (shouldPerformPublish && !shouldOpenShareAfterPublish && listOfRotations && listOfRotations.length) {
        for (const assignee of assignees) {
          if (assignee && assignee.staffingType == 'Agency') {
            await requestAssignShiftOnExternal(listOfRotations[0], assignee.id);
          }
        }
      }

      if (shouldOpenShareAfterPublish && listOfRotations.length) {
        openShiftShareDrawer({
          preselectedRecurrences: listOfRotations,
        });
      }

      handleClose();
    } catch (error) {
      showErrorIntl(parseError(error, `${localizationSubset}.errors`).message);
    }
  };

  const handleCancel = handleClose;

  const durationType = watch('durationType');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const startTime = watch('startTime');
  const endTime = watch('endTime');
  const assigneesIds = watch('assignees');
  const assigned = talents.filter((talent) => assigneesIds.includes(talent.id));
  const locationId = watch('location');
  const numberOfCopies = watch('numberOfCopies');

  const template = watch('template');

  useEffect(() => {
    if (template) {
      const selectedTemplate = shiftTemplates.find((t) => t.id === template);
      if (selectedTemplate) {
        const startValueParsed = dayjs(selectedTemplate.startTime, 'HH:mm:ss');
        const endValueParsed = startValueParsed.add(selectedTemplate.durationInSeconds || 0, 'seconds');
        const startValue = dayjs.utc({
          year: startValueParsed.year(),
          month: startValueParsed.month(),
          date: startValueParsed.date(),
          hour: startValueParsed.hour(),
          minute: startValueParsed.minute(),
          second: startValueParsed.second(),
        });
        const endValue = dayjs.utc({
          year: endValueParsed.year(),
          month: endValueParsed.month(),
          date: endValueParsed.date(),
          hour: endValueParsed.hour(),
          minute: endValueParsed.minute(),
          second: endValueParsed.second(),
        });
        setValue('startTime', startValue);
        setValue('endTime', endValue);
        setValue('durationInDays', selectedTemplate.durationInDays || null);
        const realDepartments = (currentLocation?.structure ?? []).filter(
          (dep) => dep.type === 'department' && currentLocation?.usedDepartmentNodes.includes(dep.id),
        );
        if (
          selectedTemplate.departmentNode?.id &&
          realDepartments.find((dep) => dep.id === selectedTemplate.departmentNode.id)
        ) {
          setValue('department', selectedTemplate.departmentNode.id);
          if (selectedTemplate.subDepartmentNode?.id) {
            setValue('subDepartment', selectedTemplate.subDepartmentNode.id);
            if (selectedTemplate.specialty?.id) {
              setValue('specialty', selectedTemplate.specialty.id);
            }
          }
        }
        if (selectedTemplate.shiftTimePresetTemplate?.id) {
          setValue('shiftTypePreset', selectedTemplate.shiftTimePresetTemplate.id);
        }
        if (selectedTemplate.qualifications?.length) {
          setValue(
            'qualifications',
            selectedTemplate.qualifications.map((q) => q.id),
          );
        }

        if (selectedTemplate.tags?.length) {
          setValue('tags', selectedTemplate.tags.join(', '));
        }
        if (selectedTemplate.rotationRecurrence) {
          const parsed = JSON.parse(selectedTemplate.rotationRecurrence);
          setIncomingSequence(parsed);
        }
      }
    }
  }, [template, shiftTemplates, setValue]);

  const shiftTypePreset = watch('shiftTypePreset');

  useEffect(() => {
    if (shiftTypePreset) {
      const selectedPreset = shiftTypePresets.find((t) => t.id === shiftTypePreset);
      if (selectedPreset) {
        const startValueParsed = dayjs(selectedPreset.startTime, 'HH:mm:ss');
        const endValueParsed = startValueParsed.add(selectedPreset.durationInSeconds || 0, 'seconds');
        const startValue = dayjs.utc({
          year: startValueParsed.year(),
          month: startValueParsed.month(),
          date: startValueParsed.date(),
          hour: startValueParsed.hour(),
          minute: startValueParsed.minute(),
          second: startValueParsed.second(),
        });
        const endValue = dayjs.utc({
          year: endValueParsed.year(),
          month: endValueParsed.month(),
          date: endValueParsed.date(),
          hour: endValueParsed.hour(),
          minute: endValueParsed.minute(),
          second: endValueParsed.second(),
        });

        setValue('startTime', startValue);
        setValue('endTime', endValue);
      }
    }
  }, [shiftTypePreset, setValue]);

  useEffect(() => {
    loadProviders({ filters: { locations: { key: 'locations', value: [locationId] } } });
  }, [loadProviders, locationId]);

  useEffect(() => {
    if (startDate && endDate && startDate?.toDate() > endDate?.toDate()) {
      setValue('endDate', startDate);
    }
  }, [startDate, endDate]);

  const minimalDate = dayjs(new Date()).subtract(1, 'year').startOf('day');
  const maxDate = dayjs('2099-12-31').startOf('day');

  useEffect(() => {
    if (endDate && endDate?.toDate() > maxDate.toDate()) {
      setValue('endDate', maxDate);
    }
  }, [endDate]);

  useEffect(() => {
    if (startDate && startDate?.toDate() > maxDate.toDate()) {
      setValue('startDate', maxDate);
    }
    const durationInDays = watch('durationInDays');
    if (startDate && durationInDays) {
      const newEndDate = startDate.clone().add(durationInDays, 'day');
      if (endDate && endDate.toDate() < newEndDate.toDate()) {
        setValue('endDate', newEndDate);
      } else {
        setValue('endDate', newEndDate);
      }
    }
  }, [setValue, startDate]);

  useEffect(() => {
    setValue('schedule', calcTimeSlots(startDate, endDate, startTime, endTime, sequence));
  }, [startDate, endDate, startTime, endTime, sequence, setValue]);

  useEffect(() => {
    if (currentLocationID) {
      // Set value from current location
      setValue('location', currentLocationID);
    }
  }, [setValue, currentLocationID]);

  useEffect(() => {
    if (startDate && endDate) {
      const isArchived = dayjs(startDate).isBefore(dayjs(), 'day') && dayjs(endDate).isBefore(dayjs(), 'day');
      const isSemiArchived =
        dayjs(startDate).isBefore(dayjs(), 'day') && dayjs(endDate).isAfter(dayjs().subtract(1, 'day'), 'day');
      setShowArchivedWarn(isArchived);
      setShowSemiArchivedWarn(isSemiArchived);
    }
  }, [startDate, endDate]);

  const assignee = methods.getValues('assignees');
  const shouldShowPublishNShareButton = isAddDialog;
  const shouldDisablePublishNShare = loading || assignee.length !== 0;

  return (
    <ClosableDrawer
      open={open}
      loading={loading}
      onClose={handleClose}
      showXMarkButton={false}
      slots={{
        header: (
          <Stack direction={'row'}>
            <Typography variant="h5">
              <FormattedMessage id={`${localizationSubset}.dialogHeading`} />
            </Typography>
            <Box flex={1} />
          </Stack>
        ),
      }}
      controls={
        <Stack direction={'row-reverse'} spacing={2} sx={(theme) => ({ paddingBottom: theme.spacing(1) })}>
          {shouldShowPublishNShareButton && (
            <DrawerControlButton
              variant="contained"
              onClick={handlePublishNShare}
              disabled={shouldDisablePublishNShare}
            >
              <FormattedMessage id={`${localizationSubset}.menuButtons.publishNShare.label`} />
            </DrawerControlButton>
          )}
          <MenuButton variant="outlined" onClick={handlePublish} disabled={loading}>
            <FormattedMessage id={`${localizationSubset}.menuButtons.publish.label`} />
          </MenuButton>
          <MenuButton variant="text" onClick={handleSave} disabled={loading}>
            <FormattedMessage id="common.save" />
          </MenuButton>
          <Box flex={1} />
          <MenuButton
            onClick={handleCancel}
            sx={(theme) => ({ color: theme.palette.text.secondary })}
            disabled={loading}
          >
            <FormattedMessage id={`${localizationSubset}.menuButtons.back.label`} />
          </MenuButton>
        </Stack>
      }
    >
      <Stack direction={'column'} sx={{ height: '100%' }} spacing={2} width={{ xs: '400px', sm: '600px' }}>
        <Form formUtils={methods} style={{ height: '100%' }} onSubmit={handleSubmit.bind(handleSubmit, false, false)}>
          {/* Shift configuration */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.shiftConfiguration.sectTitle`} />}>
            <Stack direction={'row'} spacing={2}>
              <FormSelect
                disabled={loading || !isAddDialog}
                label={<FormattedMessage id={`${localizationSubset}.shiftConfiguration.shiftTemplate.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.shiftConfiguration.shiftTemplate.placeholder`,
                })}
                fullWidth
                name="template"
                options={shiftTemplates
                  .filter((t) => t.recurrenceType === 'rotation')
                  .map((t) => ({
                    key: t.id,
                    label: t.name,
                    value: t.id,
                  }))}
              />
              <FormNumberField
                disabled={loading || !isAddDialog}
                label={<FormattedMessage id={`${localizationSubset}.shiftConfiguration.quantity.label`} />}
                fullWidth
                minValue={1}
                name="numberOfCopies"
              />
            </Stack>
            <FormCheckbox
              name="isUrgent"
              label={<FormattedMessage id={`${localizationSubset}.shiftConfiguration.markAsUrgent.label`} />}
              size="medium"
            />
          </FormBlock>

          {/* Date and duration */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.dateAndDuration.sectTitle`} />}>
            <Stack direction={'column'} spacing={1}>
              <FormRadioGroup
                name="durationType"
                label={<FormattedMessage id={`${localizationSubset}.dateAndDuration.durationType.label`} />}
                items={[
                  {
                    label: (
                      <FormattedMessage id={`${localizationSubset}.dateAndDuration.durationType.items.setEndDate`} />
                    ),
                    value: 'setEndDate',
                  },
                  {
                    label: (
                      <FormattedMessage id={`${localizationSubset}.dateAndDuration.durationType.items.infiniteLoop`} />
                    ),
                    value: 'infiniteLoop',
                    disabled: true,
                  },
                ]}
                required
                disabled={loading}
                row
              />
              <Grid container direction={'row'}>
                <Grid item xs={6} sx={{ paddingRight: '8px' }}>
                  <FormDatePicker
                    disabled={loading}
                    label={<FormattedMessage id={`${localizationSubset}.dateAndDuration.startDate.label`} />}
                    fullWidth
                    minDate={minimalDate}
                    maxDate={maxDate}
                    name="startDate"
                    defaultValue={null}
                    required
                  />
                </Grid>
                <Grid item xs={6} sx={{ paddingLeft: '8px' }}>
                  {durationType === 'setEndDate' && (
                    <FormDatePicker
                      disabled={loading}
                      label={<FormattedMessage id={`${localizationSubset}.dateAndDuration.endDate.label`} />}
                      fullWidth
                      minDate={dayjs(startDate || minimalDate)}
                      maxDate={maxDate}
                      name="endDate"
                      defaultValue={null}
                      required
                    />
                  )}
                </Grid>
                {showArchivedWarn && (
                  <Grid item xs={12} sx={{ paddingLeft: '8px' }}>
                    <Typography variant="caption" sx={(theme) => ({ color: theme.palette.warning.main })}>
                      <FormattedMessage id={`${localizationSubset}.dateAndDuration.archivedWarning.label`} />
                    </Typography>
                  </Grid>
                )}
                {showSemiArchivedWarn && (
                  <Grid item xs={12} sx={{ paddingLeft: '8px' }}>
                    <Typography variant="caption" sx={(theme) => ({ color: theme.palette.warning.main })}>
                      <FormattedMessage id={`${localizationSubset}.dateAndDuration.semiArchivedWarning.label`} />
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Stack>
          </FormBlock>
          {/* Series details */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.rotationDetails.sectTitle`} />}>
            <Grid container spacing={2}>
              {/* <Grid item xs={6}>
                <FormSelect
                  disabled
                  label={<FormattedMessage id={`${localizationSubset}.rotationDetails.seriesPreset.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.rotationDetails.seriesPreset.placeholder`,
                  })}
                  fullWidth
                  name="seriesPreset"
                  options={[]}
                />
              </Grid> */}
              <Grid item xs={6}>
                <FormSelect
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.rotationDetails.shiftTypePreset.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.rotationDetails.shiftTypePreset.placeholder`,
                  })}
                  fullWidth
                  name="shiftTypePreset"
                  options={shiftTypePresets.map((t) => ({
                    key: t.id,
                    label: t.name,
                    value: t.id,
                  }))}
                />
              </Grid>
              <Grid item xs={6}></Grid>
              <Grid item xs={6}>
                <FormTimePicker
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.rotationDetails.startTime.label`} />}
                  fullWidth
                  name="startTime"
                  defaultValue={null}
                  timezone="UTC"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <FormTimePicker
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.rotationDetails.endTime.label`} />}
                  fullWidth
                  name="endTime"
                  timezone="UTC"
                  defaultValue={null}
                  required
                />
              </Grid>
            </Grid>
          </FormBlock>
          {/* Rotation sequence */}
          <CollapsibleFormBlock
            sectTitle={<FormattedMessage id={`${localizationSubset}.rotationSequence.sectTitle`} />}
            expanded
            sectTitleBadges={
              <Chip
                size="small"
                label={
                  sequence.length
                    ? sequence.reduce((acc, seq, idx, array) => {
                        if (array.length === 1 && array[0].durationInDays === 0) {
                          return intl.formatMessage({
                            id: `${localizationSubset}.rotationSequence.sectBadges.noSequence`,
                          });
                        }
                        const label = seq.isPause ? 'Off' : 'On';
                        return `${acc} ${seq.durationInDays} ${label}`;
                      }, '')
                    : intl.formatMessage({ id: `${localizationSubset}.rotationSequence.sectBadges.noSequence` })
                }
              />
            }
          >
            <RotationSequenceSection
              localizationSubset={localizationSubset}
              defaultSequence={incomingSequence}
              onChange={(seq) => {
                setSequence(seq);
              }}
            />
          </CollapsibleFormBlock>
          {/* Rotation schedule preview */}
          <CollapsibleFormBlock
            sectTitle={<FormattedMessage id={`${localizationSubset}.rotationSchedulePreview.sectTitle`} />}
            expanded
            sectTitleBadges={
              <Chip
                size="small"
                label={intl.formatMessage(
                  { id: `${localizationSubset}.rotationSchedulePreview.sectBadges.spendTime` },
                  { amount: scheduledUniqueDays.length },
                )}
              />
            }
          >
            <RotationScheduleSection
              localizationSubset={localizationSubset}
              onChange={(value) => {
                setValue('schedule', value);
              }}
              slots={dateTimeSlots}
              defaultTimeSlot={{
                start: startTime,
                end: endTime,
              }}
              error={errors.schedule?.message}
            />
          </CollapsibleFormBlock>
          {/* Location */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.location.sectTitle`} />}>
            <LocationSelectionSection localizationSubset={localizationSubset} loading={loading} />
          </FormBlock>

          {/* Talent */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.talentConfiguration.sectTitle`} />}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormRadioGroup
                  name="staffingType"
                  label={<FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.label`} />}
                  items={[
                    {
                      label: <FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.items.all`} />,
                      value: 'All',
                    },
                    {
                      label: (
                        <FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.items.internal`} />
                      ),
                      value: 'Internal',
                    },
                    {
                      label: (
                        <FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.items.external`} />
                      ),
                      value: 'External',
                    },
                  ]}
                  required
                  disabled={loading}
                  row
                />
              </Grid>
              <Grid item xs={12}>
                <QualificationSelect
                  fullWidth
                  name="qualifications"
                  required
                  localizationSubset={localizationSubset}
                  disabledOptions={assigned.flatMap((a) => a.qualifications.map((q) => q.id))}
                />
              </Grid>
              <Grid item xs={12}>
                <AssigneesSelect
                  fullWidth
                  name="assignees"
                  numberOfCopies={numberOfCopies}
                  localizationSubset={localizationSubset}
                  selectedQualifications={watch('qualifications')}
                  disabled={watch('qualifications').length === 0}
                />
              </Grid>
            </Grid>
          </FormBlock>

          {/* Additional info */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.additionalInfo.sectTitle`} />}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormTextField
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.additionalInfo.tag.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.additionalInfo.tag.placeholder`,
                  })}
                  fullWidth
                  name="tags"
                />
              </Grid>
            </Grid>
          </FormBlock>
          <CollapsibleFormBlock
            sectTitle={<FormattedMessage id={`${localizationSubset}.additionalInfo.notes.sectTitle`} />}
          >
            <FormTextField
              name="notes"
              fullWidth
              multiline
              disabled={loading}
              options={{
                maxLength: 300,
              }}
              InputProps={{
                sx: { height: '150px', overflow: 'hidden' },
              }}
            />
          </CollapsibleFormBlock>
        </Form>
      </Stack>
    </ClosableDrawer>
  );
}

const MenuButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(16.75),
}));

type ShiftRotationFormData = {
  template: string;
  numberOfCopies: number;
  isUrgent: boolean;

  durationType: 'setEndDate' | 'infiniteLoop';
  durationInDays: number | null;
  startDate: Dayjs | null;
  endDate: Dayjs | null;

  seriesPreset: string;
  shiftTypePreset: string;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  schedule: ScheduleItem[];

  location: string;
  department: string;
  subDepartment: string;
  specialty: string;

  staffingType: SingleShift['staffingType'];
  qualifications: string[];
  assignees: string[];

  tags: string;
  notes: string;
};

function calcTimeSlots(
  startDate?: Dayjs | null,
  endDate?: Dayjs | null,
  startTime?: Dayjs | null,
  endTime?: Dayjs | null,
  sequence: SequenceItem[] = [],
) {
  if (!startDate || !endDate || !startTime || !endTime) {
    return [];
  }
  const slotsDays = [];
  let currentDate = startDate
    .clone()
    .set('hour', startTime?.hour() || 0)
    .set('minute', startTime?.minute() || 0);
  const seqMask = sequence.flatMap(({ isPause, durationInDays }) =>
    [...Array(durationInDays).keys()].map(() => (isPause ? 0 : 1)),
  );
  if (seqMask.length === 0) {
    return [];
  }
  let seqIndex = 0;

  while (!currentDate.isAfter(endDate.add(1, 'day'))) {
    if (seqIndex >= seqMask.length) {
      seqIndex = 0;
    }

    if (seqMask[seqIndex] === 1) {
      slotsDays.push(currentDate.clone());
    }
    seqIndex++;
    currentDate = currentDate.add(1, 'day');
  }

  return slotsDays.map((d) => ({
    id: Math.random().toString(36).slice(2, 9),
    start: d,
    end: d
      .clone()
      // Add 1 day if end time is before start time (on the next day)
      .add(startTime.isAfter(endTime) ? 1 : 0, 'day')
      .set('hour', endTime.hour() || 0)
      .set('minute', endTime.minute() || 0),
  }));
}

function checkOverlapping(dateTimeSlots: ScheduleItem[]) {
  const sortedSlots = dateTimeSlots.sort((a, b) => a.start.unix() - b.start.unix());
  for (let i = 0; i < sortedSlots.length - 1; i++) {
    if (sortedSlots[i].end.isAfter(sortedSlots[i + 1].start)) {
      return true;
    }
  }
  return false;
}

type ScheduleItem = {
  id: string;
  start: Dayjs;
  end: Dayjs;
};
